import React, { useState } from 'react';
import { userKey } from '../utils/storageHelper.js';
import { useSystemProfile } from '../utils/systemProfile.js';
import { incrementDocCount } from '../utils/docCounter.js';
import { saveDocToHistory } from '../utils/docHistory.js';
import { callLLMWithMeta } from './llmService';
import { supabase } from '../lib/supabaseClient.js';
import questionsData from '../data/risk-calculator-questions.json';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
//import html2pdf from 'html2pdf.js';

// ── Report token budget ──
// The AI report has 5 required sections (Risk Summary, Top 3 Urgent Actions,
// framework-specific clauses, Secondary Framework Considerations, Realistic
// Timeline) — 800 tokens was too tight for that and could truncate mid-section.
const REPORT_MAX_TOKENS = 2500;

function RiskCalculator({ onNavigate, returnTo }) {
  const [step, setStep] = useState(0); // 0 = intro, 1..N = questions, N+1 = results
  const [answers, setAnswers] = useState({});
  const _profile = useSystemProfile();
  const [companyName, setCompanyName] = useState(() => _profile.companyName || '');
  const [loading, setLoading] = useState(false);
  const [aiReport, setAiReport] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const questions = questionsData.questions;
  const totalSteps = questions.length;
  const RESULTS_STEP = totalSteps + 1;

  // Rehydrate prior result on mount — keeps result visible after navigation
  React.useEffect(() => {
    try {
      const savedResult = localStorage.getItem(userKey('qais_risk_result'));
      const savedAnswers = localStorage.getItem(userKey('qais_risk_answers'));
      const savedReport = localStorage.getItem(userKey('qais_risk_aiReport'));
      if (savedResult) {
        setResult(JSON.parse(savedResult));
        if (savedAnswers) setAnswers(JSON.parse(savedAnswers));
        if (savedReport) setAiReport(savedReport);
        setStep(RESULTS_STEP); // jump to results view
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Determine primary framework based on user's profile ──
  const determinePrimaryFramework = () => {
    const goal = (_profile.primaryGoal || '').toLowerCase();
    const regions = (_profile.deploymentRegions || '').toLowerCase();

    if (goal.includes('eu ai act')) return 'eu';
    if (goal.includes('nist')) return 'nist';
    if (goal.includes('iso 42001')) return 'iso';

    if (regions.includes('eu') || regions.includes('europe') || regions.includes('global')) return 'eu';
    if (regions.includes('united states') || regions.includes('us')) return 'nist';

    return 'iso';
  };

  // ── Score Calculator ──
  const calculateScore = (allAnswers) => {
    let totalRiskScore = 0;
    let isoReadiness = 50;
    let nistMaturity = 'Initial';

    questions.forEach((q) => {
      const answer = allAnswers[q.id];
      if (!answer || q.type === 'text') return;

      const selectedOption = q.options?.find(o => o.value === answer);
      if (!selectedOption) return;

      if (selectedOption.risk_score) {
        totalRiskScore += selectedOption.risk_score;
      }
      if (q.id === 'q9' && selectedOption.iso_readiness !== undefined) {
        isoReadiness = selectedOption.iso_readiness;
      }
      if (q.id === 'q9' && selectedOption.nist_maturity !== undefined) {
        nistMaturity = selectedOption.nist_maturity;
      }
    });

    const deploymentRegions = (_profile.deploymentRegions || '').toLowerCase();
    let euBonus = 0;
    if (deploymentRegions.includes('eu') || deploymentRegions.includes('europe') || deploymentRegions.includes('global')) {
      euBonus = 8;
    } else if (deploymentRegions.includes('uk')) {
      euBonus = 4;
    }
    totalRiskScore += euBonus;
    totalRiskScore = Math.min(totalRiskScore, 100);

    const levels = questionsData.scoring.risk_levels;
    let riskLevel = 'minimal';
    for (const [key, val] of Object.entries(levels)) {
      if (totalRiskScore >= val.range[0] && totalRiskScore <= val.range[1]) {
        riskLevel = key;
        break;
      }
    }

    const primaryFramework = determinePrimaryFramework();
    const nistConcernLevel = totalRiskScore >= 75 ? 'Critical' :
                             totalRiskScore >= 55 ? 'High' :
                             totalRiskScore >= 30 ? 'Medium' : 'Low';
    const euApplicable = deploymentRegions.includes('eu') ||
                         deploymentRegions.includes('europe') ||
                         deploymentRegions.includes('global');

    return {
      totalRiskScore, isoReadiness, nistMaturity, riskLevel, euBonus,
      primaryFramework, nistConcernLevel, euApplicable
    };
  };

  // ── Build summary of answers for AI prompt ──
  const buildAnswerSummary = (allAnswers) => {
    return questions.map((q) => {
      const answer = allAnswers[q.id];
      if (!answer) return null;
      if (q.type === 'text') return `${q.question}: ${answer}`;
      const opt = q.options?.find(o => o.value === answer);
      return `${q.question}: ${opt?.label || answer}`;
    }).filter(Boolean).join('\n');
  };

  // ── Detect a response that was cut off mid-generation (hit max_tokens) ──
  // Same heuristic used in the document generator: a properly finished report
  // should end in sentence-ending punctuation, a closing markdown element, or
  // a bullet/list line — not mid-word or on a dangling label/punctuation.
  const looksTruncated = (text) => {
    if (!text) return true;
    const trimmed = text.trim();
    const tail = trimmed.slice(-40);
    if (/[|\-–—:]\s*$/.test(trimmed)) return true;
    if (!/[.!?:)"'”\]]\s*$/.test(trimmed) && !/^[-*•]/.test(tail.split('\n').pop() || '')) {
      return true;
    }
    return false;
  };

  // ── Generate AI Report ──
  const generateReport = async (allAnswers, scoreResult) => {
    setLoading(true);
    setAiReport('');

    const summary = buildAnswerSummary(allAnswers);
    const level = questionsData.scoring.risk_levels[scoreResult.riskLevel];
    const fw = scoreResult.primaryFramework;
    const frameworkLabel = fw === 'eu' ? 'EU AI Act' : fw === 'nist' ? 'NIST AI RMF' : 'ISO/IEC 42001';

    const frameworkContext = fw === 'eu'
      ? `The user's PRIMARY framework is EU AI Act. They have EU deployment exposure. Lead with EU AI Act obligations specific to their classification (${level.label}). Reference specific EU AI Act articles (Art. 6, 9, 10, 14, etc.) as relevant to their answers.`
      : fw === 'nist'
      ? `The user's PRIMARY framework is NIST AI RMF (US-focused). EU AI Act is ${scoreResult.euApplicable ? 'also applicable due to EU deployment' : 'NOT applicable to this organization'}. Lead with NIST AI RMF Govern/Map/Measure/Manage functions. ${scoreResult.euApplicable ? 'Briefly mention EU AI Act considerations.' : 'Do not include EU AI Act recommendations as primary actions.'}`
      : `The user's PRIMARY framework is ISO/IEC 42001 (universally applicable AI Management System). Lead with ISO 42001 clauses (§ 5, § 6, § 7, § 8, § 9, § 10) most relevant to their gaps. Reference Annex A controls. EU AI Act is ${scoreResult.euApplicable ? 'also applicable' : 'NOT directly applicable but principles align'}.`;

    const prompt = `You are an expert AI compliance consultant. Based on the assessment below, provide a detailed but concise compliance report.

CONTEXT FOR YOUR RECOMMENDATIONS:
${frameworkContext}

ASSESSMENT FACTS:
Company: ${companyName || 'The organization'}
Risk Score: ${scoreResult.totalRiskScore}/100${scoreResult.euBonus > 0 ? ` (includes +${scoreResult.euBonus} EU geographic bonus)` : ''}
Primary Framework: ${frameworkLabel}
NIST AI RMF Concern Level: ${scoreResult.nistConcernLevel}
ISO 42001 Readiness: ${scoreResult.isoReadiness}%
NIST AI RMF Maturity: ${scoreResult.nistMaturity}
${scoreResult.euApplicable ? `EU AI Act Classification: ${level.label}` : 'EU AI Act: Not applicable to this deployment'}

ASSESSMENT ANSWERS:
${summary}

Please provide your report in this structure:

1. **Risk Summary** (2 sentences): Plain-English explanation of why this concern level was assigned, framed for ${frameworkLabel}.

2. **Top 3 Urgent Actions** (specific to ${frameworkLabel}): What they must address first, with framework references.

3. **${fw === 'eu' ? 'Key EU AI Act Articles to Address' : fw === 'nist' ? 'Priority NIST AI RMF Functions' : 'Priority ISO/IEC 42001 Clauses'}**: 2-3 specific items with brief explanation.

4. **Secondary Framework Considerations**: One sentence on how secondary frameworks (${fw === 'eu' ? 'NIST + ISO' : fw === 'nist' ? 'ISO + (EU if applicable)' : 'NIST + (EU if applicable)'}) reinforce the primary work.

5. **Realistic Timeline**: 30/60/90-day milestones to reach basic compliance.

Keep response practical and specific to their answers. Avoid generic advice. Do not invent regulations or articles that don't exist.

Formatting must be consistent throughout the entire report: always use "- " (hyphen plus space) for bullet points — never "•" or "*" as a bullet marker. Always use "**Label:**" (double asterisk) to bold a label — never a single asterisk like "*Label:*". Do not mix styles within the same report.`;

    try {
      const first = await callLLMWithMeta({
        messages: [
          {
            role: 'system',
            content: 'You are an AI compliance expert specializing in EU AI Act, ISO/IEC 42001, and NIST AI RMF. You give specific, actionable, framework-aware compliance guidance. You do NOT recommend EU AI Act compliance for organizations without EU exposure unless they explicitly want it. You do NOT invent regulation citations. Formatting must be consistent throughout: always use "- " for bullets and "**Label:**" (double asterisk) for bolded labels — never "•", a single asterisk, or a mix of styles.'
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.5,
        max_tokens: REPORT_MAX_TOKENS
      });

      let content = first.content;
      const firstProvider = first.provider; // which provider answered — used to prefer it on a continuation

      // ── If the response looks cut off, ask the model to finish it rather than
      // silently handing the user a broken report. One continuation attempt,
      // preferring the same provider that wrote the first half so the tone and
      // formatting stay consistent (falls back to the rest of the chain if that
      // provider is no longer available). ──
      if (looksTruncated(content)) {
        try {
          const continuation = await callLLMWithMeta({
            messages: [
              {
                role: 'system',
                content: 'You are an AI compliance expert. Never invent regulation citations. Formatting must stay consistent with the text already generated below: match its existing bullet marker ("- ") and bold style ("**Label:**") exactly rather than switching conventions partway through.'
              },
              { role: 'user', content: prompt },
              { role: 'assistant', content },
              { role: 'user', content: 'Your previous response was cut off before it finished. Continue exactly where you left off — do not repeat any earlier content, and do not restart the report.' },
            ],
            temperature: 0.5,
            max_tokens: REPORT_MAX_TOKENS,
            preferredProvider: firstProvider,
          });
          content = content + continuation.content;
        } catch (contErr) {
          console.warn('Continuation attempt failed, keeping original (possibly truncated) report:', contErr);
        }
      }

      setAiReport(content);
      try { localStorage.setItem(userKey('qais_risk_aiReport'), content); } catch {}
    } catch (error) {
      setAiReport('❌ Unable to generate AI report at this time. Please review the compliance actions below.');
    }
    setLoading(false);
  };

  // ── Handle Answer Selection ──
  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  // ── Handle Next ──
  const handleNext = () => {
    const currentQ = questions[step - 1];
    if (!answers[currentQ.id] || answers[currentQ.id].trim() === '') return;

    if (step === totalSteps) {
      const scoreResult = calculateScore(answers);
      setResult(scoreResult);
      setStep(RESULTS_STEP);
      incrementDocCount();
      saveDocToHistory({
        tool: 'risk_calculator',
        title: `AI Risk Assessment${companyName ? ' — ' + companyName : ''}`,
        preview: `Risk Level: ${scoreResult.riskLevel}. Score: ${scoreResult.totalRiskScore}/100. ISO Readiness: ${scoreResult.isoReadiness}%`,
        framework: 'EU AI Act / ISO 42001 / NIST RMF',
      });


      try { localStorage.setItem(userKey('qais_risk_result'), JSON.stringify(scoreResult)); } catch {}
      try { localStorage.setItem(userKey('qais_risk_answers'), JSON.stringify(answers)); } catch {}

      // Persist server-side — this is the real source of truth for journey-completion
      // gating (e.g. QLedger eligibility). localStorage alone is user-editable and
      // must never be trusted for that decision.
      (async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await supabase.from('user_documents').insert({
              user_id:      user.id,
              doc_type:     'risk_assessment',
              content:      JSON.stringify(scoreResult),
              framework:    'EU AI Act / ISO 42001 / NIST RMF',
              generated_at: new Date().toISOString(),
            });
          }
        } catch (e) {
          console.log('Supabase risk assessment save failed; localStorage retains it:', e.message);
        }
      })();

      window.dispatchEvent(new CustomEvent('qais-journey-update'));
      generateReport(answers, scoreResult);



    } else {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(prev => prev - 1);
    else setStep(0);
  };

  const handleReset = () => {
    setStep(0);
    setAnswers({});
    setCompanyName(_profile.companyName || '');
    setResult(null);
    setAiReport('');
    try {
      localStorage.removeItem(userKey('qais_risk_result'));
      localStorage.removeItem(userKey('qais_risk_answers'));
      localStorage.removeItem(userKey('qais_risk_aiReport'));
    } catch {}
  };

  // ── Styled PDF download with logo cover + every-page header/footer ──
// ── Helper: load image as base64 (for embedding in jsPDF + HTML) ──
  const loadImageAsBase64 = async (path) => {
    try {
      const response = await fetch(path);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('Logo load failed:', e);
      return null;
    }
  };

  // ── Convert a block of Markdown-table lines into an HTML <table> ──
  const renderMarkdownTable = (lines) => {
    // lines: array of raw '| a | b |' strings, including the '|---|---|' separator row
    const parseRow = (line) =>
      line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

    const rows = lines
      .map(parseRow)
      // drop the '---' separator row
      .filter((cells) => !cells.every((c) => /^:?-{2,}:?$/.test(c)));

    if (rows.length === 0) return '';

    const [headerRow, ...bodyRows] = rows;
    const headerHtml = `<tr>${headerRow
      .map(
        (cell) =>
          `<th style="border: 1px solid #cbd5e1; background: #e2e8f0; color: #0e7c7c; padding: 6px 10px; text-align: left; font-size: 13px;">${cell}</th>`
      )
      .join('')}</tr>`;

    const bodyHtml = bodyRows
      .map(
        (cells) =>
          `<tr>${cells
            .map(
              (cell) =>
                `<td style="border: 1px solid #cbd5e1; padding: 6px 10px; font-size: 13px; vertical-align: top;">${cell}</td>`
            )
            .join('')}</tr>`
      )
      .join('');

    return `<table style="border-collapse: collapse; width: 100%; margin: 10px 0 16px 0;">${headerHtml}${bodyHtml}</table>`;
  };

  // ── Helper: render AI markdown cleanly for both screen + PDF ──
  // Removes visible markdown artifacts (#, *, =, _, leading -) while preserving structure.
  // Headings, including numbered headings such as "1. **Risk Summary**", are dark cyan, bold, and underlined.
  const renderAiMarkdown = (text) => {
    if (!text) return '';

    const escapeHtml = (value) => String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    let escaped = escapeHtml(text);

    // ── Pull out Markdown table blocks first and replace with real <table> HTML,
    // so raw '|' syntax never reaches the plain-text formatting below. Escaping
    // above only touches &, <, >, ", ' — pipe characters pass through untouched,
    // so this still works on the escaped string. ──
    const lines = escaped.split('\n');
    const outLines = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const isTableRow = /^\s*\|.*\|\s*$/.test(line);
      const nextIsSeparator = i + 1 < lines.length && /^\s*\|?[\s:|-]*-{2,}[\s:|-]*\|?\s*$/.test(lines[i + 1]);
      if (isTableRow && nextIsSeparator) {
        const tableLines = [];
        while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
          tableLines.push(lines[i]);
          i++;
        }
        outLines.push(renderMarkdownTable(tableLines));
      } else {
        outLines.push(line);
        i++;
      }
    }
    escaped = outLines.join('\n');

    return escaped
      // Remove setext-style heading underline/separator rows made of ===, ---, or ___
      .replace(/^\s*[=\-_]{3,}\s*$/gm, '')
      // Numbered markdown headings: 1. **Risk Summary** or 1. Risk Summary

     .replace(/^\s*(\d+\.\s*)\*\*([^*]+?)\*\*(.*)$/gm, '<div style="margin: 16px 0 8px 0;"><span style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 18px;">$1$2</span>$3</div>')
     .replace(/^\s*(\d+\.\s*)((?:Risk Summary|Top 3 Urgent Actions|Key EU AI Act Articles to Address|Priority NIST AI RMF Functions|Priority ISO\/IEC 42001 Clauses|Secondary Framework Considerations|Realistic Timeline))(.*)$/gmi, '<div style="margin: 16px 0 8px 0;"><span style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 18px;">$1$2</span>$3</div>')

      // Hash markdown headings
      .replace(/^#{3}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 16px; margin: 14px 0 6px 0;">$1</div>')
      .replace(/^#{2}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 18px; margin: 16px 0 8px 0;">$1</div>')
      .replace(/^#{1}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 20px; margin: 18px 0 10px 0;">$1</div>')
      // Bullet lines — the model inconsistently uses "-", "*", or a literal "•" as the
      // prefix. Normalize all three to the same "• " marker so bullets look uniform
      // regardless of which style this particular response happened to use.
      .replace(/^\s*[•*-]\s+(.+)$/gm, '<div style="margin-left: 18px; margin-bottom: 4px;">• $1</div>')
      // Bold text: **bold** — remove ** and style emphasized text dark cyan + underline
      .replace(/\*\*(.+?)\*\*/g, '<span style="color: #0e7c7c; text-decoration: underline; font-weight: bold;">$1</span>')
      // Bold text: *bold* (single asterisk) — the model sometimes emphasizes labels this
      // way instead of "**...**". Render it identically to double-asterisk bold rather
      // than leaving the literal "*" characters on the page. Runs after the double-
      // asterisk pass above, so by this point no "**" pairs remain to conflict with.
      .replace(/\*([^*\n]+?)\*/g, '<span style="color: #0e7c7c; text-decoration: underline; font-weight: bold;">$1</span>')
      // Italic/underscore markdown: remove underscores while preserving the text
      .replace(/(?<![\w_])_([^_\n]+)_(?![\w_])/g, '<em>$1</em>')
      // Cleanup extra blank lines and line breaks around generated blocks
      .replace(/<\/div>\n+/g, '</div>')
      .replace(/<\/table>\n+/g, '</table>')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\n/g, '<br>')
      // A <table> that got wrapped in <br> tags on either side reads oddly — trim them
      .replace(/<br>\s*(<table)/g, '$1')
      .replace(/(<\/table>)\s*<br>/g, '$1');
  };

  // ── Helper: build numbered Q&A blocks (HTML) for the assessment section ──
  const buildAnswerBlocksHTML = (allAnswers) => {
    const items = questions.map((q) => {
      const answer = allAnswers[q.id];
      if (!answer) return null;
      let answerLabel = answer;
      if (q.type !== 'text') {
        const opt = q.options?.find(o => o.value === answer);
        answerLabel = opt?.label || answer;
      }
      return { question: q.question, answer: answerLabel };
    }).filter(Boolean);
  return items.map((it, i) => `
      <div style="background: #f8fafc; border-left: 4px solid #0e7c7c; padding: 14px 18px; margin-bottom: 18px; border-radius: 6px; page-break-inside: avoid;">
        <div style="font-weight: 700; color: #0e7c7c; font-size: 15px; margin-bottom: 6px;">Q${i + 1}. ${it.question}</div>
        <div style="font-size: 16px; color: #1e293b;"><strong>Answer:</strong> ${it.answer}</div>
      </div>
    `).join('');
  };

  // ── Styled PDF download with logo cover + every-page header/footer ──
const downloadStyledPDF = async () => {
  if (!result) {
    alert('No assessment result. Please complete the assessment first.');
    return;
  }
  if (!aiReport || aiReport.trim() === '') {
    alert('AI guidance is still generating. Please wait a moment and try again.');
    return;
  }
      setPdfGenerating(true);  // ← ADDED THIS
        // Yield to browser to paint the disabled/loading state before heavy work
  await new Promise(resolve => setTimeout(resolve, 50));
  const logoBase64 = await loadImageAsBase64('/logo.png');

  const level = questionsData.scoring.risk_levels[result.riskLevel];
  const actions = questionsData.compliance_actions[result.riskLevel] || [];
  const answersHTML = buildAnswerBlocksHTML(answers);

  const fw = result.primaryFramework;
  const frameworkLabel = fw === 'eu' ? 'EU AI Act' : fw === 'nist' ? 'NIST AI RMF' : 'ISO/IEC 42001';

  const primaryActions = actions.filter(a => {
    const f = (a.framework || '').toLowerCase();
    if (fw === 'eu')   return f.includes('eu') || f.includes('article');
    if (fw === 'nist') return f.includes('nist');
    if (fw === 'iso')  return f.includes('iso');
    return true;
  });
  const actionsToShow = primaryActions.length > 0 ? primaryActions : actions;

  const htmlContent = `
    <div style="font-family: 'Inter', 'Helvetica', sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white; color: #1e293b; line-height: 1.6; font-size: 18px;">
      ${logoBase64 ? `<div style="text-align: center; margin-bottom: 8px;"><img src="${logoBase64}" style="max-width: 180px; max-height: 90px;" alt="QuantumAI Shield" /></div>` : ''}
      <h1 style="color: #0e7c7c; border-bottom: 3px solid #0e7c7c; padding-bottom: 10px; font-size: 32px; text-align: center;">AI Risk Assessment Report</h1>
      <p style="text-align: center;"><strong>Generated by QuantumAI Shield</strong><br>${new Date().toLocaleDateString()}</p>
      <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 26px;">Company & Framework</h2>
      <p><strong>Company:</strong> ${companyName || 'Not specified'}<br>
      <strong>Primary Framework:</strong> ${frameworkLabel}</p>
      <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 26px;">Risk Summary</h2>
      <p><strong>Risk Score:</strong> ${result.totalRiskScore}/100 ${result.euBonus > 0 ? `(includes +${result.euBonus} EU geographic bonus)` : ''}<br>
      <strong>ISO 42001 Readiness:</strong> ${result.isoReadiness}%<br>
      <strong>NIST AI RMF Maturity:</strong> ${result.nistMaturity || 'Initial'}<br>
      <strong>NIST AI RMF Concern Level:</strong> ${result.nistConcernLevel}<br>
      ${result.euApplicable ? `<strong>EU AI Act Classification:</strong> ${level.label} (${level.eu_ai_act_ref})` : 'EU AI Act: Not applicable to this deployment'}</p>
      <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 26px;">Assessment Answers</h2>
      ${answersHTML}
      <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 26px;">Priority Compliance Actions (${frameworkLabel})</h2>
      <ul style="font-size: 18px;">${actionsToShow.map(a => `<li><strong>[${a.priority.toUpperCase()}]</strong> ${a.action}<br><span style="font-size: 14px;">Framework: ${a.framework} – Deadline: ${a.deadline}</span></li>`).join('')}</ul>
      <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 26px;">AI‑Generated Compliance Guidance</h2>
      <div style="background: #f1f5f9; padding: 16px; border-radius: 12px; font-size: 16px; line-height: 1.6; word-wrap: break-word;">${renderAiMarkdown(aiReport)}</div>
      <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 26px;">Disclaimer</h2>
      <p style="font-size: 14px; color: #64748b;">${questionsData.meta.disclaimer || 'AI-generated guidance is for informational purposes only and does not constitute legal advice. Always consult a qualified compliance professional.'}</p>
    </div>
  `;

const container = document.createElement('div');
  // Position off-screen so user doesn't see the render container during PDF generation
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '800px';            // matches the max-width of the HTML
  container.style.backgroundColor = 'white';
  container.style.zIndex = '-1';
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  try {
    await new Promise(resolve => setTimeout(resolve, 300));
    const canvas = await html2canvas(container, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: false,
    });

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth  = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // ── Margins (mm) — increased by ~2 lines ──
    const HEADER_HEIGHT = 28;   // was 18, now 28 (extra room for logo + title centered)
    const FOOTER_HEIGHT = 22;   // was 14, now 22
    const SIDE_MARGIN   = 14;   // mm — page-level left/right margin (was 0, edge-to-edge)

    const usableWidth  = pdfWidth - 2 * SIDE_MARGIN;
    const usableHeight = pdfHeight - HEADER_HEIGHT - FOOTER_HEIGHT;

    const imgWidth  = usableWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;


// ── Smart slicing: detect blank rows in canvas to slice at clean boundaries ──
    const canvasCtx = canvas.getContext('2d');
    const imageData = canvasCtx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const cw = canvas.width;

    const isRowBlank = (y) => {
        // A row is "safe to slice at" if it has very few dark pixels
        let darkPixels = 0;
        for (let x = 0; x < cw; x += 4) {
          const i = (y * cw + x) * 4;
          if (data[i] < 100 || data[i + 1] < 100 || data[i + 2] < 100) {
            darkPixels++;
            if (darkPixels > 2) return false; // 3+ dark pixels = has text
          }
        }
        return true;
      };

    const pxPerMm = canvas.height / imgHeight;
    const usableHeightPx = Math.floor(usableHeight * pxPerMm);

    // Sample blank rows for break candidates
    const blankRows = [];
    for (let y = 0; y < canvas.height; y += 2) {
      if (isRowBlank(y)) blankRows.push(y);
    }

    // Compute page break points
    const pageBreaks = [0];
    let currentY = 0;
    while (currentY < canvas.height) {
      const target = currentY + usableHeightPx;
      if (target >= canvas.height) break;
      let breakAt = target;
      const minAllowed = currentY + usableHeightPx * 0.35;
        for (let i = blankRows.length - 1; i >= 0; i--) {
          if (blankRows[i] <= target && blankRows[i] > minAllowed) {
            breakAt = blankRows[i];
            break;
          }
        }
      pageBreaks.push(breakAt);
      currentY = breakAt;
    }

    // Drop trailing blank pages (no meaningful content after last break)
    let lastContentRow = 0;
    for (let y = canvas.height - 1; y >= 0; y -= 2) {
      if (!isRowBlank(y)) { lastContentRow = y; break; }
    }
    while (pageBreaks.length > 1 && pageBreaks[pageBreaks.length - 1] >= lastContentRow - 20) {
      pageBreaks.pop();
    }
    const totalPages = pageBreaks.length;


    // Helper: draw header + footer on the current page (CENTERED)
const drawHeaderFooter = (pageNum) => {
      // ── Header — only on page 2 onwards (page 1 has the big cover logo) ──
      if (pageNum > 1) {
        const titleText = 'Quantum AI Shield · AI Risk Assessment';
        pdf.setFontSize(11);
        pdf.setTextColor(80, 80, 80);
        const logoW  = logoBase64 ? 10 : 0;
        const gap    = logoBase64 ? 3  : 0;
        const titleW = pdf.getTextWidth(titleText);
        const groupW = logoW + gap + titleW;
        const groupStartX = (pdfWidth - groupW) / 2;

        if (logoBase64) {
          try {
            pdf.addImage(logoBase64, 'PNG', groupStartX, 6, 10, 10);
          } catch {}
        }
        pdf.text(titleText, groupStartX + logoW + gap, 13);

        // Separator line below header (also only on page 2+)
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.2);
        pdf.line(SIDE_MARGIN + 4, HEADER_HEIGHT - 4, pdfWidth - SIDE_MARGIN - 4, HEADER_HEIGHT - 4);
      }

      // ── Footer ──
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.line(SIDE_MARGIN + 4, pdfHeight - FOOTER_HEIGHT + 6, pdfWidth - SIDE_MARGIN - 4, pdfHeight - FOOTER_HEIGHT + 6);
      pdf.text('www.QuantumAIShield.com',                   SIDE_MARGIN + 4,           pdfHeight - 8);
      pdf.text(`Page ${pageNum} of ${totalPages}`,           pdfWidth / 2,              pdfHeight - 8, { align: 'center' });
      pdf.text(new Date().toLocaleDateString(),              pdfWidth - SIDE_MARGIN - 4, pdfHeight - 8, { align: 'right' });
    };

// ── For each page, cut the canvas to that page's slice and add it ──
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      if (pageNum > 1) pdf.addPage();

      const startPx = pageBreaks[pageNum - 1];
      const endPx   = pageNum < totalPages ? pageBreaks[pageNum] : canvas.height;
      const slicePx = endPx - startPx;
      const sliceMm = slicePx / pxPerMm;

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width  = canvas.width;
      tempCanvas.height = slicePx;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.fillStyle = '#ffffff';
      tempCtx.fillRect(0, 0, canvas.width, slicePx);
      tempCtx.drawImage(canvas, 0, startPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
      const sliceData = tempCanvas.toDataURL('image/png');

      pdf.addImage(sliceData, 'PNG', SIDE_MARGIN, HEADER_HEIGHT, imgWidth, sliceMm);
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pdfWidth, HEADER_HEIGHT, 'F');
      pdf.rect(0, pdfHeight - FOOTER_HEIGHT, pdfWidth, FOOTER_HEIGHT, 'F');
      drawHeaderFooter(pageNum);
    }

    pdf.save(`${companyName || 'AI'}_Risk_Assessment_${Date.now()}.pdf`);
  } catch (err) {
    console.error('PDF generation error:', err);
    alert('Failed to generate PDF. Please try again.');
  } finally {
    document.body.removeChild(container);
    setPdfGenerating(false);  // ← ADD THIS
  }
};

  const currentQuestion = step >= 1 && step <= totalSteps ? questions[step - 1] : null;
  const progress = step === 0 ? 0 : Math.round((step / totalSteps) * 100);
  const riskColors = {
    unacceptable: { bg: '#fef2f2', border: '#ef4444', text: '#991b1b', badge: '#ef4444' },
    high: { bg: '#fffbeb', border: '#f59e0b', text: '#92400e', badge: '#f59e0b' },
    limited: { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af', badge: '#3b82f6' },
    minimal: { bg: '#f0fdf4', border: '#10b981', text: '#065f46', badge: '#10b981' },
  };

  // ── INTRO SCREEN ──
  if (step === 0) {
    return (
      <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>⚖️</div>
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#0f172a', marginBottom: '10px' }}>AI Risk Score Calculator</h1>
          <p style={{ fontSize: '22px', color: '#64748b', marginBottom: '30px' }}>{totalSteps} questions · ~3 minutes · Multi-framework risk classification</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          {[
            { icon: '🎯', title: 'Primary Framework Focus', desc: 'Tailored to your EU AI Act / NIST / ISO 42001 priority' },
            { icon: '📋', title: 'ISO 42001 Readiness', desc: 'Your current readiness percentage score' },
            { icon: '✅', title: 'Action Plan', desc: 'Prioritized compliance steps with deadlines' },
            { icon: '🤖', title: 'AI-Generated Report', desc: 'Personalized guidance from your answers' },
          ].map((item) => (
            <div key={item.title} style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{item.icon}</div>
              <p style={{ fontWeight: '600', color: '#0f172a', marginBottom: '4px', fontSize: '14px' }}>{item.title}</p>
              <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>{item.desc}</p>
            </div>
          ))}
        </div>

        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '8px', fontSize: '16px' }}>Company Name</label>
          <input
            type="text"
            value={companyName}
            disabled
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              backgroundColor: '#f1f5f9',
              cursor: 'not-allowed',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <button
          onClick={() => setStep(1)}
          style={{ width: '100%', padding: '18px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '12px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}
        >
          Start Assessment ➤
        </button>

        <div style={{ marginTop: '20px', padding: '14px', backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '10px' }}>
          <p style={{ margin: 0, color: '#92400e', fontSize: '13px' }}><strong>⚠️ Disclaimer:</strong> {questionsData.meta.disclaimer}</p>
        </div>
      </div>
    );
  }

  // ── RESULTS SCREEN ──
  if (step === RESULTS_STEP && result) {
    const level = questionsData.scoring.risk_levels[result.riskLevel];
    const actions = questionsData.compliance_actions[result.riskLevel] || [];
    const colors = riskColors[result.riskLevel] || riskColors['minimal'];

    const fw = result.primaryFramework;
    const frameworkLabel = fw === 'eu' ? 'EU AI Act' : fw === 'nist' ? 'NIST AI RMF' : 'ISO/IEC 42001';
    const frameworkIcon = fw === 'eu' ? '🇪🇺' : fw === 'nist' ? '🏛️' : '📋';

    const primaryBannerText = fw === 'eu'
      ? `EU AI Act: ${level.label}`
      : fw === 'nist'
      ? `NIST AI RMF Concern: ${result.nistConcernLevel}`
      : `ISO/IEC 42001: ${result.isoReadiness}% readiness`;

    const primaryDescription = fw === 'eu'
      ? level.description
      : fw === 'nist'
      ? `Based on your assessment, your AI system requires ${result.nistConcernLevel.toLowerCase()}-level risk management attention under NIST AI RMF Govern/Map/Measure/Manage functions.`
      : `Your ISO/IEC 42001 readiness reflects your current AI Management System maturity. ${result.isoReadiness >= 70 ? 'Strong foundation in place.' : result.isoReadiness >= 40 ? 'Several gaps to close before certification.' : 'Significant work needed to establish AIMS basics.'}`;

    return (
      <div style={{ padding: '40px', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ backgroundColor: colors.bg, border: `3px solid ${colors.border}`, borderRadius: '16px', padding: '30px', textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>{frameworkIcon}</div>
          <p style={{ fontSize: '13px', color: colors.text, fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Your Primary Framework</p>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: colors.text, marginBottom: '8px' }}>{primaryBannerText}</h1>
          {companyName && <p style={{ fontSize: '16px', color: colors.text, marginBottom: '8px' }}>{companyName}</p>}
          <p style={{ fontSize: '15px', color: colors.text, marginBottom: '20px', lineHeight: '1.6' }}>{primaryDescription}</p>
          <div style={{ display: 'inline-block', backgroundColor: colors.badge, color: 'white', padding: '6px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold' }}>{frameworkLabel}</div>
        </div>

        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '30px' }}>
          <p style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>Also Evaluated</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
            {fw !== 'nist' && (
              <div style={{ padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#64748b' }}>🏛️ NIST AI RMF</p>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{result.nistConcernLevel} concern · {result.nistMaturity}</p>
              </div>
            )}
            {fw !== 'iso' && (
              <div style={{ padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#64748b' }}>📋 ISO/IEC 42001</p>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{result.isoReadiness}% readiness</p>
              </div>
            )}
            {fw !== 'eu' && (
              <div style={{ padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#64748b' }}>🇪🇺 EU AI Act</p>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: result.euApplicable ? '#0f172a' : '#94a3b8' }}>
                  {result.euApplicable ? `${level.label} (applicable)` : 'Not applicable'}
                </p>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '30px' }}>
          <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>Risk Score</p>
            <p style={{ fontSize: '36px', fontWeight: 'bold', color: colors.badge, margin: 0 }}>{result.totalRiskScore}<span style={{ fontSize: '16px', color: '#94a3b8' }}>/100</span></p>
            {result.euBonus > 0 && <p style={{ fontSize: '11px', color: '#7c3aed', margin: '4px 0 0' }}>+{result.euBonus} EU geographic bonus</p>}
          </div>
          <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>ISO 42001 Readiness</p>
            <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#10b981', margin: 0 }}>{result.isoReadiness}<span style={{ fontSize: '16px', color: '#94a3b8' }}>%</span></p>
          </div>
          <div style={{ backgroundColor: 'white', border: '2px solid #a5f3fc', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>NIST AI RMF Maturity</p>
            <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#06b6d4', margin: 0 }}>{result.nistMaturity || 'Initial'}</p>
            <p style={{ fontSize: '11px', color: '#94a3b8', margin: '4px 0 0' }}>Maturity Level</p>
          </div>
          <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>Action Items</p>
            <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#8b5cf6', margin: 0 }}>{actions.length}</p>
          </div>
        </div>

        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#0f172a', marginBottom: '20px' }}>✅ Required Compliance Actions</h2>
          {actions.map((action, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', padding: '14px', marginBottom: '10px', backgroundColor: action.priority === 'critical' ? '#fef2f2' : action.priority === 'high' ? '#fffbeb' : '#f0fdf4', borderRadius: '10px', border: `2px solid ${action.priority === 'critical' ? '#fecaca' : action.priority === 'high' ? '#fde68a' : '#bbf7d0'}` }}>
              <div style={{ minWidth: '80px' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', padding: '3px 8px', borderRadius: '10px', backgroundColor: action.priority === 'critical' ? '#ef4444' : action.priority === 'high' ? '#f59e0b' : '#10b981', color: 'white' }}>
                  {action.priority.toUpperCase()}
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: '600', color: '#0f172a', margin: '0 0 4px', fontSize: '15px' }}>{action.action}</p>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 2px' }}>📋 {action.framework}</p>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>⏰ {action.deadline}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#0f172a', marginBottom: '16px' }}>🤖 AI-Generated Compliance Guidance</h2>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>⏳</div>
              <p>Generating personalized compliance report...</p>
            </div>
          ) : (
            <div
              style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '10px', border: '2px solid #e2e8f0', fontFamily: 'Inter, Helvetica, sans-serif', fontSize: '14px', lineHeight: '1.8', color: '#1e293b', wordWrap: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: renderAiMarkdown(aiReport) }}
            />
          )}
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '8px' }}>
            <p style={{ margin: 0, color: '#92400e', fontSize: '13px' }}><strong>⚠️ Disclaimer:</strong> {questionsData.meta.disclaimer}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button
          onClick={downloadStyledPDF}
          disabled={loading || !aiReport || pdfGenerating}
          style={{
            flex: 1, padding: '14px',
            backgroundColor: (loading || !aiReport || pdfGenerating) ? '#cbd5e1' : '#3b82f6',
            color: 'white', border: 'none', borderRadius: '10px',
            fontSize: '16px', fontWeight: 'bold',
            cursor: pdfGenerating ? 'wait' : (loading || !aiReport) ? 'not-allowed' : 'pointer'
          }}
        >
          {pdfGenerating ? '⏳ Generating PDF...' : '⬇️ Download as PDF'}
        </button>
          <button
            onClick={handleReset}
            style={{ flex: 1, padding: '14px', backgroundColor: 'white', color: '#334155', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
          >
            🔄 Re-run Assessment
          </button>
          {returnTo === 'checklist' && onNavigate && (
            <button
              onClick={() => onNavigate('checklist')}
              style={{ flex: 1, padding: '14px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              ✏️ Back to Checklist
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── QUESTION SCREEN ──
  if (!currentQuestion) return null;
  const currentAnswer = answers[currentQuestion.id] || '';
  const canProceed = currentAnswer.trim() !== '';

  return (
    <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '14px', color: '#64748b' }}>Question {step} of {totalSteps}</span>
          <span style={{ fontSize: '14px', color: '#64748b' }}>{progress}% complete</span>
        </div>
        <div style={{ height: '8px', backgroundColor: '#e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, backgroundColor: '#3b82f6', borderRadius: '10px', transition: 'width 0.3s ease' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
          {questions.map((_, idx) => (
            <div key={idx} style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: idx + 1 < step ? '#10b981' : idx + 1 === step ? '#3b82f6' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', color: idx + 1 <= step ? 'white' : '#94a3b8' }}>
              {idx + 1 < step ? '✓' : idx + 1}
            </div>
          ))}
        </div>
      </div>

      <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '32px', marginBottom: '24px' }}>
        <span style={{ display: 'inline-block', backgroundColor: '#eff6ff', color: '#1e40af', fontSize: '12px', fontWeight: '600', padding: '4px 12px', borderRadius: '20px', marginBottom: '16px' }}>
          {currentQuestion.category}
        </span>
        <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#0f172a', marginBottom: '8px', lineHeight: '1.4' }}>
          {currentQuestion.question}
        </h2>
        {currentQuestion.hint && <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px', fontStyle: 'italic' }}>💡 {currentQuestion.hint}</p>}

        {currentQuestion.type === 'text' && (
          <textarea
            value={currentAnswer}
            onChange={e => handleAnswer(currentQuestion.id, e.target.value)}
            placeholder={currentQuestion.placeholder}
            rows={4}
            style={{ width: '100%', padding: '14px', fontSize: '16px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: '1.6' }}
          />
        )}

        {currentQuestion.type === 'dropdown' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {currentQuestion.options.map((option) => (
              <div
                key={option.value}
                onClick={() => handleAnswer(currentQuestion.id, option.value)}
                style={{
                  padding: '16px 20px',
                  borderRadius: '10px',
                  border: `2px solid ${currentAnswer === option.value ? '#3b82f6' : '#e2e8f0'}`,
                  backgroundColor: currentAnswer === option.value ? '#eff6ff' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: `2px solid ${currentAnswer === option.value ? '#3b82f6' : '#cbd5e1'}`, backgroundColor: currentAnswer === option.value ? '#3b82f6' : 'white', flexShrink: 0, marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {currentAnswer === option.value && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'white' }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: '600', color: currentAnswer === option.value ? '#1e40af' : '#0f172a', margin: '0 0 4px', fontSize: '15px' }}>{option.label}</p>
                    {option.note && (
                      <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: '1.4' }}>
                        {option.eu_ai_act_ref && <span style={{ color: '#7c3aed', fontWeight: '600' }}>{option.eu_ai_act_ref} — </span>}
                        {option.note}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={handleBack} style={{ padding: '14px 28px', backgroundColor: 'white', color: '#334155', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>← Back</button>
        <button
          onClick={handleNext}
          disabled={!canProceed}
          style={{ flex: 1, padding: '14px', backgroundColor: canProceed ? '#3b82f6' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: canProceed ? 'pointer' : 'not-allowed', transition: 'background-color 0.2s' }}
        >
          {step === totalSteps ? '📊 Calculate My Risk Score' : 'Next →'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
        {currentQuestion.frameworks.map(fw => (
          <span key={fw} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>{fw}</span>
        ))}
      </div>
    </div>
  );
}

export default RiskCalculator;